"use client";
import React from "react";
import { Settings, TrendingUp, User } from "lucide-react";
import Image, { StaticImageData } from "next/image";
import {
  Avatar1,
  Avatar2,
  Avatar3,
  Avatar4,
  Avatar5,
  Avatar6,
} from "@/assets/img/landing";

const professionals = [
  { name: "Sarah John", image: Avatar1 },
  { name: "Michael Scott", image: Avatar2 },
  { name: "John Cena", image: Avatar3 },
];
const professionalsSecondRow = [
  { name: "Lukas Müller", image: Avatar4 },
  { name: "Felix Weber", image: Avatar5 },
  { name: "Jonas Schneider", image: Avatar6 },
];

const stats = [
  { value: "16,311", percentage: "8.5%" },
  { value: "40,689", percentage: "8.5%" },
  { value: "8,132", percentage: "2.12%" },
];

const ProfessionalCard = ({
  name,
  image,
}: {
  name: string;
  image: StaticImageData;
}) => (
  <div className="flex items-center gap-2 bg-white rounded-full pl-2 pr-4 py-2 flex-shrink-0 min-w-fit">
    <Image
      src={image}
      alt={name}
      width={36}
      height={36}
      className="rounded-full object-cover flex-shrink-0"
    />
    <span className="text-sm font-medium text-[#2d3a6b] whitespace-nowrap">
      {name}
    </span>
    <div className="w-5 h-5 rounded-full bg-[#378ADD] flex items-center justify-center flex-shrink-0">
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="white"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </div>
  </div>
);

const TrustedResults = (): React.JSX.Element => {
  const [statIndex, setStatIndex] = React.useState(0);
  const [fade, setFade] = React.useState(true);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setStatIndex((i) => (i + 1) % stats.length);
        setFade(true);
      }, 400);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="w-full max-w-[1480px] mx-auto px-4 sm:px-12 py-12 sm:py-16">
      <div className="rounded-2xl px-4 sm:px-8 py-6 mb-8 text-center">
        <h2 className="font-bold text-[#373A74] tracking-tight text-[clamp(18px,3vw,38px)]">
          Trusted Results That Drive Impact Across
          <br className="hidden sm:block" /> Industries and Organisations
        </h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div
          className="rounded-3xl p-8 sm:p-14 flex flex-col gap-4 bg-[#F0F6E5] relative
          lg:pb-36 lg:mb-[130px] lg:overflow-visible overflow-hidden"
        >
          <div>
            <h3 className="text-[clamp(22px,3vw,40px)] font-bold text-[#2d3a6b]">
              EU-Grade Expertise
            </h3>
            <p className="text-[clamp(14px,1.5vw,20px)] text-gray-500 mt-1">
              Irish-led, offshore-delivered for competitive pricing.
            </p>
          </div>

          <div
            className="bg-white rounded-2xl p-5 sm:p-6 flex flex-col gap-3
            lg:absolute lg:bottom-0 lg:translate-y-1/2 lg:left-[10%] lg:w-[475px] lg:shadow-lg"
          >
            <div className="flex justify-between items-center">
              <span className="text-[clamp(14px,1.5vw,25px)] text-gray-400">
                Users Supported
              </span>
              <div className="w-12 h-12 rounded-2xl bg-[#EAE8F8] flex items-center justify-center">
                <User size={24} color="#2d3a6b" />
              </div>
            </div>
            <p
              className={`text-[clamp(28px,3vw,44px)] font-bold text-[#2d3a6b] leading-none transition-opacity duration-300 ${
                fade ? "opacity-100" : "opacity-0"
              }`}
            >
              {stats[statIndex].value}
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <TrendingUp color="#1D9E75" size={18} />
              <span
                className={`text-[clamp(14px,1.5vw,25px)] font-semibold text-[#1D9E75] transition-opacity duration-300 ${
                  fade ? "opacity-100" : "opacity-0"
                }`}
              >
                {stats[statIndex].percentage}
              </span>
              <span className="text-[clamp(12px,1.2vw,20px)] text-gray-400">
                Increase in Adoption
              </span>
            </div>
          </div>
        </div>

        <div
          className="rounded-3xl p-8 sm:p-14 flex flex-col gap-4 bg-[#DFF0FC] relative
          lg:pb-36 lg:mb-[150px] lg:overflow-visible overflow-hidden"
        >
          <div>
            <h3 className="text-[clamp(22px,3vw,40px)] font-bold text-[#2d3a6b]">
              Rapid Prototyping
            </h3>
            <p className="text-[clamp(14px,1.5vw,20px)] text-gray-500 mt-1">
              Working software in 1 week, not 6.
            </p>
          </div>

          <div
            className="w-full rounded-2xl overflow-hidden bg-white
            lg:absolute lg:bottom-0 lg:translate-y-1/2 lg:left-[10%] lg:w-[475px] lg:shadow-lg"
          >
            <video
              className="w-full h-full object-cover scale-[1.030]"
              src="/PointA.mp4"
              autoPlay
              muted
              loop
              playsInline
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:mt-[130px]">
        <div className="rounded-3xl py-10 sm:py-16 flex flex-col gap-5 bg-[#E0E9F7] overflow-hidden">
          <div className="px-8 sm:px-16">
            <h3 className="text-[clamp(22px,3vw,40px)] font-bold text-[#2d3a6b]">
              Certified Professionals
            </h3>
            <p className="text-[clamp(14px,1.5vw,20px)] text-gray-500 mt-1">
              CISSP, CISM, ISO Lead Auditors ensuring quality.
            </p>
          </div>
          <div className="flex w-full flex-col gap-3 item-center">
            <div className="overflow-hidden">
              <div className="marquee-track-left">
                {professionals.map((p, i) => (
                  <ProfessionalCard key={i} {...p} />
                ))}
              </div>
            </div>
            <div className="overflow-hidden">
              <div className="marquee-track-right">
                {professionalsSecondRow.map((p, i) => (
                  <ProfessionalCard key={`r${i}`} {...p} />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-3xl p-8 sm:p-14 flex flex-col bg-[#E7E7EE]">
          <div>
            <h3 className="text-[clamp(22px,3vw,40px)] font-bold text-[#2d3a6b]">
              Grant-Ready
            </h3>
            <p className="text-[clamp(14px,1.5vw,20px)] text-gray-500 mt-1">
              Enterprise Ireland applications handled efficiently.
            </p>
          </div>
          <div className="flex items-end justify-start gap-4 pt-6 mt-auto">
            <div className="gear-swing">
              <Settings
                size={80}
                color="#2d3a6b"
                fill="#2d3a6b"
                strokeWidth={1}
                stroke="#EAEAEA"
              />
            </div>
            <div className="gear-swing">
              <Settings size={100} color="#2d3a6b" strokeWidth={1.2} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustedResults;
